import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type CSSProperties, type RefObject } from "react";
import { useLocation, useNavigate } from "react-router";

/**
 * 通知（#168、docs/10 §5.12）：全画面で1つの置き場。操作の結果（保存した・取り込んだ・検算の結果など）はここに出す。
 *   置き場所：右下（幅 767px 以下は画面の下・横いっぱい）。同時に3つまで（4つ目を出すと古いものから消える）。
 *   成功・情報・注意：15秒で薄くなって消える（0.2秒）。残りの時間は下の細い線（3px）。
 *     マウスを乗せている間・中にフォーカスがある間は止まる（離すと残りから）。× と Esc で閉じる。
 *   誤り（保存できない など）：消えない（× か Esc で閉じるまで残る）。線は出さない。role="alert"（ほかは role="status"）。
 *   欄の入力の誤りは通知にしない（その欄の下に ErrorText）。画面の状態（件数・一致など）は見出しの右のチップ（.chip）。
 *   動きを減らす設定（prefers-reduced-motion）では、出る・消える・線の動きを止める（時間がたつと消えるのは同じ）。
 *   画面を移ると、前の画面で出した知らせは消える（誤りは残る。画面＝App の Authed の page・製品の置き場の画面の道＝useNoticeScreen。
 *   同じ画面の中の窓・シートの URL（/settings/bank-accounts ⇄ /new）では消さない。前の画面からの一言は state.notice で渡す＝移った先で出る）。
 *   状態は React の外の小さな入れ物に持つ＝どの部品からでも notify() で出せる。描くのは App の NoticeHost 1か所
 */

/** 薄くなり始めるまで（成功・情報・注意） */
export const NOTICE_MS = 15_000;
/** 薄くなって消えるまで（styles.css の .notice-card.is-leaving の transition と同じ長さ） */
export const NOTICE_FADE_MS = 200;
/** 同時に出す数 */
export const NOTICE_MAX = 3;

export type NoticeTone = "success" | "info" | "warn" | "error";
export type NoticeAction = { label: string; onClick: () => void };
export type NoticeInput = {
  text: string;
  /** 既定は success */
  tone?: NoticeTone | undefined;
  /** 「元に戻す」などのボタン（押すと閉じる） */
  action?: NoticeAction | undefined;
  /** 同じ key の通知が出ていれば、それを置き換える（同じ知らせを重ねない） */
  key?: string | undefined;
  testId?: string | undefined;
};
/** screen：出したときの画面（useNoticeScreen）。画面を移ると、誤りでない通知は消える（前のトーストと同じ。誤りは × まで残す） */
export type NoticeItem = NoticeInput & { id: number; tone: NoticeTone; screen: string };

let seq = 0;
let items: readonly NoticeItem[] = [];
/** いまの画面（useNoticeScreen が入れる） */
let screen = "";
const subs = new Set<() => void>();
const emit = () => {
  for (const f of subs) f();
};
const subscribe = (f: () => void) => {
  subs.add(f);
  return () => {
    subs.delete(f);
  };
};
const snapshot = () => items;

/** 通知を出す。返り値の番号で dismissNotice できる */
export function notify(input: NoticeInput | string): number {
  const x: NoticeInput = typeof input === "string" ? { text: input } : input;
  const id = ++seq;
  const item: NoticeItem = { ...x, id, tone: x.tone ?? "success", screen };
  const rest = x.key ? items.filter((n) => n.key !== x.key) : items;
  items = [...rest, item].slice(-NOTICE_MAX);
  emit();
  return id;
}
/** 誤りの通知（消えない） */
export const notifyError = (text: string, key?: string): number => notify({ text, tone: "error", key });

export function dismissNotice(id: number): void {
  if (!items.some((n) => n.id === id)) return;
  items = items.filter((n) => n.id !== id);
  emit();
}
/**
 * いまの画面を知らせる（App の Authed・Registered が呼ぶ。key は page か道）。画面が変わったら、前の画面で出した知らせ（誤りでないもの）を消す。
 * レイアウトの効果で入れる＝新しい画面の部品の useEffect が出す知らせは新しい画面のもの
 */
export function useNoticeScreen(key: string): void {
  useLayoutEffect(() => {
    if (screen === key) return;
    screen = key;
    const next = items.filter((n) => n.tone === "error" || n.screen === key);
    if (next.length === items.length) return;
    items = next;
    emit();
  }, [key]);
}

const reduceMotion = () => !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/** 1つの通知。時間・止める・閉じるはこの中で持つ */
function NoticeCard({ n }: { n: NoticeItem }) {
  const sticky = n.tone === "error";
  const [held, setHeld] = useState(false);
  const [leaving, setLeaving] = useState(false);
  /** 線のアニメーションを始めからにし直す（薄くなる途中で止めたとき） */
  const [round, setRound] = useState(0);
  const hover = useRef(false);
  const focus = useRef(false);
  const left = useRef(NOTICE_MS);
  const startedAt = useRef(0);
  const timer = useRef<number | undefined>(undefined);
  const fade = useRef<number | undefined>(undefined);
  const el = useRef<HTMLDivElement>(null);

  const stop = () => {
    window.clearTimeout(timer.current);
    timer.current = undefined;
  };
  const leave = () => {
    stop();
    if (reduceMotion()) {
      dismissNotice(n.id);
      return;
    }
    setLeaving(true);
    fade.current = window.setTimeout(() => dismissNotice(n.id), NOTICE_FADE_MS);
  };
  const run = () => {
    if (sticky) return;
    stop();
    startedAt.current = Date.now();
    timer.current = window.setTimeout(leave, left.current);
  };
  const hold = () => {
    if (sticky) return;
    if (timer.current !== undefined) left.current = Math.max(0, left.current - (Date.now() - startedAt.current));
    stop();
    if (fade.current !== undefined) {
      // 薄くなっている途中で乗せた：消すのをやめて、離したらもう一度 15秒
      window.clearTimeout(fade.current);
      fade.current = undefined;
      setLeaving(false);
      left.current = NOTICE_MS;
      setRound((r) => r + 1);
    }
    setHeld(true);
  };
  const release = () => {
    if (hover.current || focus.current || sticky) return;
    setHeld(false);
    run();
  };

  useEffect(() => {
    run();
    return () => {
      stop();
      window.clearTimeout(fade.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const close = () => {
    // 閉じたあとフォーカスが行き場を失わないように、通知の中にあったら外す
    if (el.current?.contains(document.activeElement)) (document.activeElement as HTMLElement | null)?.blur();
    dismissNotice(n.id);
  };

  return (
    <div
      ref={el}
      className={`notice-card is-${n.tone}` + (held ? " is-held" : "") + (leaving ? " is-leaving" : "")}
      role={sticky ? "alert" : "status"}
      data-testid={n.testId ?? "notice"}
      data-tone={n.tone}
      onMouseEnter={() => {
        hover.current = true;
        hold();
      }}
      onMouseLeave={() => {
        hover.current = false;
        release();
      }}
      onFocus={() => {
        focus.current = true;
        hold();
      }}
      onBlur={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        focus.current = false;
        release();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          close();
        }
      }}
    >
      <span className="notice-text" data-testid="notice-text">
        {n.text}
      </span>
      {n.action ? (
        <button
          type="button"
          className="notice-act"
          onClick={() => {
            n.action?.onClick();
            dismissNotice(n.id);
          }}
          data-testid="notice-action"
        >
          {n.action.label}
        </button>
      ) : null}
      <button type="button" className="notice-x" aria-label="通知を閉じる" title="閉じる" onClick={close} data-testid="notice-close">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>
      {sticky ? null : <i key={round} className="notice-life" aria-hidden="true" style={{ animationDuration: `${NOTICE_MS}ms` }} data-testid="notice-life" />}
    </div>
  );
}

/**
 * 下に固定のボタンの列（保存のバー・作成画面の主のボタン・スマホの下のボタン・引き出しの下の段 など）の印。#171
 * #172：スマホの下から出るシートの枠にも付ける（共通の Sheet・スマホの見積の SheetFrame・工程表・協力会社のページ・書類を選ぶシート・出力の窓）。
 * 開いているあいだ、通知はシートの上の端より上に出る（シートのボタンに重ならない）
 */
export const NOTICE_ABOVE_ATTR = "data-notice-above";

/**
 * 通知を下に固定のボタンの列の上に出す（#171）：画面の下の端に付いている [data-notice-above] の要素で、通知の置き場と横に重なるものの
 * 上の端まで通知を持ち上げる（px）。出ているあいだだけ、大きさ・スクロール・要素の出入りで測り直す（1フレームに1回）。
 * #172：下から上がるシートは動き（transition・animation）の終わりでも測り直す。「下の端」は見えている範囲（visualViewport）の下＝
 * キーボードの上に持ち上げたシートも数える。背の高いシート（スマホの見積の行のシートなど、上に 40px しか空かない）で通知の束が
 * 上に入りきらないときは、そのシートでは持ち上げない（画面の上にはみ出させない・シートの頭の「完了」「閉じる」に重ねない）
 */
function useNoticeLift(on: boolean, box: RefObject<HTMLDivElement | null>): number {
  const [lift, setLift] = useState(0);
  useLayoutEffect(() => {
    if (!on) {
      setLift(0);
      return;
    }
    let raf = 0;
    const measure = () => {
      raf = 0;
      const h = window.innerHeight;
      // 見えている範囲の下の端（スマホのキーボードが出ていれば、その上）
      const vv = window.visualViewport;
      const vb = vv ? Math.min(h, vv.offsetTop + vv.height) : h;
      const r0 = box.current?.getBoundingClientRect();
      // 通知の置き場の横の範囲（測れなければ右下 400px）
      const left = r0 ? r0.left : window.innerWidth - 416;
      const right = r0 ? r0.right : window.innerWidth - 16;
      // 通知の束が入る高さ（画面の上に 8px あける）
      const room = h - (box.current?.offsetHeight ?? 0) - 8;
      let m = 0;
      for (const el of document.querySelectorAll<HTMLElement>(`[${NOTICE_ABOVE_ATTR}]`)) {
        const r = el.getBoundingClientRect();
        if (!r.width || !r.height) continue; // 隠れている
        if (r.bottom < vb - 24 || r.top >= vb) continue; // 画面の下に付いていない（下から上がる途中で、まだ画面の外も）
        if (r.right <= left || r.left >= right) continue; // 通知と横に重ならない
        const need = Math.ceil(h - r.top);
        if (need > room) continue; // 上に入りきらない背の高いシート
        m = Math.max(m, need);
      }
      setLift((v) => (v === m ? v : m));
    };
    const ask = () => {
      if (!raf) raf = window.requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("resize", ask);
    window.addEventListener("scroll", ask, true);
    window.visualViewport?.addEventListener("resize", ask);
    window.visualViewport?.addEventListener("scroll", ask);
    // シートが下から上がる動き・閉じる動きの終わり（要素の出入りの知らせは動きの始めにしか来ない）
    document.addEventListener("transitionend", ask, true);
    document.addEventListener("animationend", ask, true);
    const mo = new MutationObserver(ask);
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["class", "style", "hidden", NOTICE_ABOVE_ATTR] });
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", ask);
      window.removeEventListener("scroll", ask, true);
      window.visualViewport?.removeEventListener("resize", ask);
      window.visualViewport?.removeEventListener("scroll", ask);
      document.removeEventListener("transitionend", ask, true);
      document.removeEventListener("animationend", ask, true);
      mo.disconnect();
    };
  }, [on, box]);
  return lift;
}

/** フォーカスが文字を打つ欄・開いている窓の中に無いか（Esc を通知に使ってよいか） */
function escFree(): boolean {
  const a = document.activeElement as HTMLElement | null;
  if (a && a !== document.body) {
    if (a.closest(".notices")) return true;
    if (a.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName)) return false;
  }
  return !document.querySelector('[aria-modal="true"], dialog[open]');
}

/**
 * 通知の置き場（App に1つ）。前の画面から渡された一言（navigate の state.notice）もここで1回だけ出す
 * （state.recreate のように「元に戻す」が要る一言は、その画面の useFlash が出す＝state.noticeAction を付けない）
 */
export function NoticeHost() {
  const list = useSyncExternalStore(subscribe, snapshot, snapshot);
  const loc = useLocation();
  const nav = useNavigate();
  const seen = useRef<string | null>(null);

  useEffect(() => {
    const st = loc.state as { notice?: unknown; noticeTestId?: unknown; recreate?: unknown } | null;
    if (typeof st?.notice !== "string" || st.recreate || seen.current === loc.key) return;
    seen.current = loc.key;
    // noticeTestId：移る前の画面で出していた通知と同じ印（発行の直後に紙の画面へ移るときの "issued-notice"。2026-10-22）
    notify({ text: st.notice, tone: "success", testId: typeof st.noticeTestId === "string" ? st.noticeTestId : "flash-notice" });
    // 一言は1回だけ（読み込み直し・戻るで2回出さない）。ほかの state（fresh など）は残す
    const { notice: _n, noticeTestId: _t, ...rest } = st;
    nav(loc.pathname + loc.search + loc.hash, { replace: true, state: Object.keys(rest).length ? rest : null });
  }, [loc, nav]);

  // Esc：文字を打つ欄・窓の外にいるときは、いちばん新しい通知を閉じる（通知の中の Esc はその通知）
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented || !items.length || !escFree()) return;
      dismissNotice(items[items.length - 1]!.id);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const box = useRef<HTMLDivElement>(null);
  const lift = useNoticeLift(list.length > 0, box);

  if (!list.length) return null;
  return (
    <div className={"notices" + (lift ? " is-lifted" : "")} role="region" aria-label="通知" data-testid="notices" ref={box} style={lift ? ({ "--notice-lift": `${lift}px` } as CSSProperties) : undefined}>
      {list.map((n) => (
        <NoticeCard key={n.id} n={n} />
      ))}
    </div>
  );
}
